import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type QueryParamScalar = string | number | boolean;
export type QueryParams = Record<string, QueryParamScalar | ReadonlyArray<QueryParamScalar>>;

export interface RequestOptions {
  params?: QueryParams;
  headers?: Record<string, string>;
}

/**
 * Root HTTP facade. All feature services must use this — never inject
 * HttpClient or reference environment.apiUrl outside of this file.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiUrl;

  private url(path: string): string {
    return `${this.base}/${path.replace(/^\/+/, '')}`;
  }

  get<T>(path: string, options?: RequestOptions): Observable<T> {
    return this.http.get<T>(this.url(path), {
      params: options?.params,
      headers: options?.headers,
    });
  }

  post<T>(path: string, body: unknown, options?: RequestOptions): Observable<T> {
    return this.http.post<T>(this.url(path), body, {
      params: options?.params,
      headers: options?.headers,
    });
  }

  put<T>(path: string, body: unknown, options?: RequestOptions): Observable<T> {
    return this.http.put<T>(this.url(path), body, {
      headers: options?.headers,
    });
  }

  patch<T>(path: string, body: unknown, options?: RequestOptions): Observable<T> {
    return this.http.patch<T>(this.url(path), body, {
      params: options?.params as Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>> | undefined,
      headers: options?.headers,
    });
  }

  delete<T>(path: string): Observable<T> {
    return this.http.delete<T>(this.url(path));
  }

  upload<T>(path: string, formData: FormData): Observable<T> {
    return this.http.post<T>(this.url(path), formData);
  }
}
